import React from 'react';
import { useForm } from 'react-hook-form';
import ConstituentSearchFilterField from '@/constants/ConstituentSearchFilterField';
import CustomFieldTypes from '@/constants/customFields/CustomFieldTypes';
import FilterOperator from '@/constants/forms/FilterOperator';
import useConstituent from '@/lib/hooks/swr/useConstituent';
import useCustomFields from '@/lib/hooks/swr/useCustomFields';
import usePreselectedLabels from '@/lib/hooks/swr/usePreselectedLabels';
import { useQueryProvider } from '@/modules/QueryProvider';
import type AdvancedConstituentSearchFilter from '@/types/AdvancedConstituentSearchFilter';
import type Label from '@/types/Label';
import { dateStringToDate } from '@/utils/dates';
import isConstituentId from '@/utils/isConstituentID';
import type AdvancedFilterFormFilter from '../types/AdvancedFilterFormFilter';
import type ConstituentSearchFilterParams from '../types/ConstituentSearchFilterParams';
import isMoneyField from '../utils/isMoneyField';

export const ADVANCED_FILTERS = 'advancedFilters';
export const DONATION_AGG = 'donationAgg';

const {
  CONSTITUENT_LABEL_IDS,
  CONNECTION_CONSTITUENT_IDS,
  CONNECTION_EMPLOYER_CONSTITUENT_IDS,
  CONNECTION_HOUSEHOLD_CONSTITUENT_IDS,
  DOB,
  DONATION_LABEL_IDS,
  DONATION_LIFETIME_TOTAL_AMOUNT,
  DONATION_THIS_YEAR_TOTAL_AMOUNT,
  DONATION_LAST_YEAR_TOTAL_AMOUNT,
  DONATION_AMOUNT,
  DONATION_LIFETIME_TOTAL_TIME,
  DONATION_THIS_YEAR_TOTAL_TIME,
  DONATION_LAST_YEAR_TOTAL_TIME,
  DONATION_MINUTES,
  IN_HONOR,
  DONATION_DATE,
} = ConstituentSearchFilterField;

type FormValues = {
  [ADVANCED_FILTERS]: AdvancedFilterFormFilter[];
  [DONATION_AGG]: boolean;
};

const getDateFilter = (filter: AdvancedConstituentSearchFilter) => {
  if (filter.operator === FilterOperator.BETWEEN) {
    if (!Array.isArray(filter.value) || filter.value.length !== 2) {
      throw new Error(
        `invalid 'value' for ${filter.field} in query builder query provider`,
      );
    }

    return {
      ...filter,
      value: filter.value.map(dateStringToDate),
    };
  }

  return {
    ...filter,
    value: [dateStringToDate(filter.value as string)],
  };
};

const useConstituentSearchAdvancedFilter = () => {
  const { clearQuery, query, updateQuery } =
    useQueryProvider<ConstituentSearchFilterParams>();
  const { data: customFields } = useCustomFields();

  const queryAdvancedFilters: AdvancedConstituentSearchFilter[] = React.useMemo(
    () =>
      ADVANCED_FILTERS in query && Array.isArray(query[ADVANCED_FILTERS])
        ? query[ADVANCED_FILTERS]
        : [],
    [query],
  );

  const labelIds = React.useMemo(
    () =>
      queryAdvancedFilters.reduce<string[]>(
        (arr, { field, value }) =>
          (field === CONSTITUENT_LABEL_IDS || field === DONATION_LABEL_IDS) &&
          Array.isArray(value)
            ? [...new Set([...arr, ...value])]
            : arr,
        [],
      ),
    [queryAdvancedFilters],
  );
  const { data: labels } = usePreselectedLabels(labelIds);

  const getFieldValue = (field: ConstituentSearchFilterField) =>
    queryAdvancedFilters.find((filter) => filter.field === field)?.value;
  const connectionId = getFieldValue(CONNECTION_CONSTITUENT_IDS);
  const employerConnectionId = getFieldValue(
    CONNECTION_EMPLOYER_CONSTITUENT_IDS,
  );
  const householdConnectionId = getFieldValue(
    CONNECTION_HOUSEHOLD_CONSTITUENT_IDS,
  );
  const inHonorConnectionValue = getFieldValue(IN_HONOR);

  const { data: connection } = useConstituent(connectionId);
  const { data: employerConnection } = useConstituent(employerConnectionId);
  const { data: householdConnection } = useConstituent(householdConnectionId);
  const { data: inHonorConnection } = useConstituent(inHonorConnectionValue);

  const donationAgg = query[DONATION_AGG] ?? false;

  const defaultValues = React.useMemo(
    () => ({
      [DONATION_AGG]: donationAgg,
      [ADVANCED_FILTERS]: queryAdvancedFilters.map(
        (filter): AdvancedFilterFormFilter => {
          if (
            filter.operator === FilterOperator.IS_NULL ||
            filter.operator === FilterOperator.IS_NOT_NULL
          ) {
            return { field: filter.field, operator: filter.operator };
          }

          if (
            filter.isCustomField &&
            customFields &&
            customFields.find(
              (customField) => customField.customFieldId === filter.field,
            )?.fieldType === CustomFieldTypes.DATE_PICKER
          ) {
            return getDateFilter(filter);
          }
          switch (filter.field) {
            case CONSTITUENT_LABEL_IDS:
            case DONATION_LABEL_IDS: {
              if (!labels?.length) {
                return {
                  ...filter,
                  value: [],
                };
              }

              const value = labels.filter((label: Label) =>
                (filter.value as string[]).includes(label.labelId),
              );

              const updatedFilter = {
                ...filter,
                value,
              };

              return updatedFilter;
            }

            case DOB:
            case DONATION_DATE:
              return getDateFilter(filter);
            case CONNECTION_CONSTITUENT_IDS:
              return { ...filter, value: connection };
            case CONNECTION_EMPLOYER_CONSTITUENT_IDS:
              return { ...filter, value: employerConnection };
            case CONNECTION_HOUSEHOLD_CONSTITUENT_IDS:
              return { ...filter, value: householdConnection };
            case IN_HONOR:
              if (isConstituentId(String(filter.value))) {
                return { ...filter, value: inHonorConnection };
              }

              return filter as AdvancedFilterFormFilter;
            case DONATION_LIFETIME_TOTAL_AMOUNT:
            case DONATION_THIS_YEAR_TOTAL_AMOUNT:
            case DONATION_LAST_YEAR_TOTAL_AMOUNT:
            case DONATION_AMOUNT:
            case DONATION_LIFETIME_TOTAL_TIME:
            case DONATION_THIS_YEAR_TOTAL_TIME:
            case DONATION_LAST_YEAR_TOTAL_TIME:
            case DONATION_MINUTES: {
              const divisor = isMoneyField(filter.field) ? 100 : 60;

              if (filter.operator === FilterOperator.BETWEEN) {
                if (!Array.isArray(filter.value) || filter.value.length !== 2) {
                  throw new Error(
                    `invalid 'value' for ${filter.field} in query builder query provider`,
                  );
                }

                filter.value.forEach((val) => {
                  if (typeof val !== 'number') {
                    throw new Error(
                      `invalid 'value' for ${filter.field} in query builder query provider`,
                    );
                  }
                });

                return {
                  ...filter,
                  value: filter.value.map((val) => val / divisor),
                } as AdvancedFilterFormFilter;
              }

              if (typeof filter.value !== 'number') {
                throw new Error(
                  `invalid 'value' for ${filter.field} in query builder query provider`,
                );
              }

              return {
                ...filter,
                value: [(filter.value as number) / divisor],
              };
            }
            default:
              return filter as AdvancedFilterFormFilter;
          }
        },
      ),
    }),
    [
      connection,
      customFields,
      donationAgg,
      employerConnection,
      householdConnection,
      labels,
      queryAdvancedFilters,
    ],
  );

  const formMethods = useForm<FormValues>({
    defaultValues,
  });

  const { reset } = formMethods;

  React.useEffect(() => {
    reset(defaultValues);
  }, [defaultValues]);
  return { clearQuery, formMethods, updateQuery };
};

export default useConstituentSearchAdvancedFilter;
